import type { BrowserNotification } from "@redotech/effect-api-shared/models/team/user-notification";
import { useHandler } from "@redotech/react-util/hook";
import {
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSubscribe } from "../stream/stream-provider";

interface BrowserNotificationContextValue {
  canNotify: boolean;
  isChannelEnabled: boolean;
  isSupported: boolean;
  permission: NotificationPermission;
  requestPermission(): Promise<NotificationPermission>;
}

const BrowserNotificationContext =
  createContext<BrowserNotificationContextValue | null>(null);
BrowserNotificationContext.displayName = "BrowserNotificationContext";

// Shared across every BrowserNotificationProvider instance (tasks, support-v2,
// …) so the same notification fired to multiple open tabs only shows once.
const browserNotificationDeduplicationChannel = "redo-browser-notifications";

function notificationPermission(): NotificationPermission {
  if (!("Notification" in globalThis)) {
    return "denied";
  }

  return Notification.permission;
}

function getDeduplicationBroadcastChannel(): BroadcastChannel | null {
  if (!("BroadcastChannel" in globalThis)) {
    return null;
  }

  return new BroadcastChannel(browserNotificationDeduplicationChannel);
}

function isBrowserNotification(event: unknown): event is BrowserNotification {
  return (
    typeof event === "object" &&
    event !== null &&
    "_tag" in event &&
    event._tag === "browserNotification"
  );
}

const SEEN_SET_MAX_SIZE = 1000;

function addToSeenSet(set: Set<string>, id: string): void {
  if (set.size >= SEEN_SET_MAX_SIZE) {
    const first = set.values().next().value;
    if (first !== undefined) {
      set.delete(first);
    }
  }
  set.add(id);
}

export const BrowserNotificationProvider = memo(
  function BrowserNotificationProvider({
    children,
    isChannelEnabled = true,
  }: {
    children: ReactNode;
    isChannelEnabled?: boolean;
  }) {
    const isSupported = "Notification" in globalThis;
    const [permission, setPermission] = useState<NotificationPermission>(() =>
      notificationPermission(),
    );
    const seenNotificationIdsRef = useRef(new Set<string>());
    const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

    useEffect(() => {
      const channel = getDeduplicationBroadcastChannel();
      broadcastChannelRef.current = channel;
      if (!channel) {
        return;
      }

      const onMessage = (event: MessageEvent<string>) => {
        addToSeenSet(seenNotificationIdsRef.current, event.data);
      };
      channel.addEventListener("message", onMessage);

      return () => {
        channel.removeEventListener("message", onMessage);
        channel.close();
      };
    }, []);

    const requestPermission = useMemo(() => {
      return async () => {
        if (!isSupported) {
          return "denied" as const;
        }

        const nextPermission = await Notification.requestPermission();
        setPermission(nextPermission);
        return nextPermission;
      };
    }, [isSupported]);

    const onEvent = useHandler((event: unknown) => {
      if (!isBrowserNotification(event)) {
        return;
      }

      if (!isChannelEnabled || !isSupported) {
        return;
      }

      if (seenNotificationIdsRef.current.has(event.notificationId)) {
        return;
      }

      if (permission === "default") {
        void requestPermission();
        return;
      }

      if (permission !== "granted") {
        return;
      }

      addToSeenSet(seenNotificationIdsRef.current, event.notificationId);
      broadcastChannelRef.current?.postMessage(event.notificationId);
      showBrowserNotification(event);
    });

    useSubscribe({ channel: "user-notifications", onEvent });

    const contextValue = useMemo<BrowserNotificationContextValue>(() => {
      return {
        canNotify: isSupported && isChannelEnabled && permission === "granted",
        isChannelEnabled,
        isSupported,
        permission,
        requestPermission,
      };
    }, [isChannelEnabled, isSupported, permission, requestPermission]);

    return (
      <BrowserNotificationContext.Provider value={contextValue}>
        {children}
      </BrowserNotificationContext.Provider>
    );
  },
);

function showBrowserNotification(notification: BrowserNotification): void {
  const browserNotification = new Notification(notification.title, {
    body: notification.body,
    requireInteraction: notification.requireInteraction,
    tag: notification.tag,
  });

  browserNotification.onclick = () => {
    globalThis.focus?.();
    if (notification.route.startsWith("/")) {
      globalThis.location.assign(notification.route);
    }
  };
}

export function useBrowserNotificationChannel(): BrowserNotificationContextValue {
  const context = useContext(BrowserNotificationContext);
  if (!context) {
    throw new Error(
      "useBrowserNotificationChannel must be used within BrowserNotificationProvider",
    );
  }

  return context;
}
